import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

function PaynowVerificationPanel({ onChanged }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const loadPayments = async () => {
    try {
      setLoading(true);
      const suffix = statusFilter ? `?status=${statusFilter}` : "";
      const response = await axiosInstance.get(`fees/paynow-payments/${suffix}`);
      setPayments(response.data || []);
    } catch (error) {
      toast.error("Failed to load Paynow verification queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [statusFilter]);

  const checkStatus = async (payment) => {
    try {
      setLoading(true);
      await axiosInstance.post(`fees/paynow-payments/${payment.id}/check-status/`);
      toast.success("Paynow status checked.");
      await loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not check Paynow status.");
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (payment) => {
    if (!window.confirm(`Verify ${payment.reference} and create official receipt?`)) return;
    try {
      setLoading(true);
      await axiosInstance.post(`fees/paynow-payments/${payment.id}/verify/`, {
        notes: "Verified from Paynow verification queue.",
      });
      toast.success("Payment verified and receipt created.");
      await loadPayments();
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not verify Paynow payment.");
    } finally {
      setLoading(false);
    }
  };

  const rejectPayment = async (payment) => {
    const reason = window.prompt("Reason for rejecting this payment proof:", "Proof could not be verified.");
    if (reason === null) return;
    try {
      setLoading(true);
      await axiosInstance.post(`fees/paynow-payments/${payment.id}/reject/`, { reason });
      toast.success("Payment request rejected.");
      await loadPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reject Paynow payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h5 className="fw-bold mb-0">Paynow Verification Queue</h5>
          <small className="text-muted">Review parent-initiated Paynow payments and activate them after confirmation.</small>
        </div>
        <div className="d-flex gap-2">
          <select className="form-select form-select-sm" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PROOF_SUBMITTED">Proof Submitted</option>
            <option value="PAID">Paid by Paynow</option>
            <option value="VERIFIED">Verified</option>
            <option value="REJECTED">Rejected</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <button className="btn btn-sm btn-light border" onClick={loadPayments} disabled={loading}>Refresh</button>
        </div>
      </div>
      <div className="card-body">
        <div className="alert alert-light border">
          <strong>Recommended control:</strong> Paynow can show whether payment was initiated/paid, but the official school receipt is only created when finance verifies the record here.
        </div>
        <div className="table-responsive">
          <table className="table align-middle">
            <thead className="table-light">
              <tr>
                <th>Reference</th><th>Student</th><th>Invoice</th><th>Amount</th><th>Parent</th><th>Status</th><th>Proof</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan="8" className="text-center text-muted py-4">No Paynow payment requests found.</td></tr>
              ) : payments.map((payment) => (
                <tr key={payment.id}>
                  <td><div className="fw-semibold">{payment.reference}</div><small className="text-muted">{payment.paynow_status || payment.method}</small></td>
                  <td><div className="fw-semibold">{payment.student_name}</div><small className="text-muted">{payment.student_number || "-"}</small></td>
                  <td>{payment.invoice_number}</td>
                  <td>{payment.currency_code} {Number(payment.amount || 0).toFixed(2)}</td>
                  <td>{payment.parent_name}</td>
                  <td><Status value={payment.status} /></td>
                  <td>{payment.proof_url ? <a href={payment.proof_url} target="_blank" rel="noreferrer">View proof</a> : <span className="text-muted">No proof</span>}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <button className="btn btn-sm btn-light border" onClick={() => checkStatus(payment)} disabled={loading || !payment.poll_url}>Check</button>
                      <button className="btn btn-sm btn-success" onClick={() => verifyPayment(payment)} disabled={loading || payment.status === "VERIFIED"}>Verify</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => rejectPayment(payment)} disabled={loading || payment.status === "VERIFIED"}>Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Status({ value }) {
  const status = String(value || "").toUpperCase();
  const cls = status === "VERIFIED" || status === "PAID" ? "text-bg-success" : status.includes("REJECT") || status.includes("FAIL") || status.includes("CANCEL") ? "text-bg-danger" : status.includes("PENDING") || status.includes("PROOF") ? "text-bg-warning" : "text-bg-secondary";
  return <span className={`badge ${cls}`}>{String(value || "-").replaceAll("_", " ")}</span>;
}

export default PaynowVerificationPanel;
